import { NotificationUi } from '../models/notification-ui';
import { reportToTrackjs } from '../../../utils/report-to-trackjs';
import { NotificationService } from '../../../services/notification.service';
import { inject, Injectable } from '@angular/core';
import { createStore } from '@ngneat/elf';
import {
  getAllEntitiesApply,
  selectAllEntities,
  selectEntitiesCount,
  selectEntitiesCountByPredicate,
  setEntities,
  UIEntitiesRef,
  updateAllEntities,
  withEntities,
  withUIEntities,
} from '@ngneat/elf-entities';
import { subscribeOnce } from '@tmx/core';
import { AccountsApi, CompanyOrder } from '@tmx/data-access';
import { shareReplay } from 'rxjs/operators';
import { getCurrentAuthenticatedUser } from '@tmx/auth';

const PENDING_STATUS = 'Pending'; // case-sensitive!
const LOCAL_STORAGE_KEY = 'notificationRepositoryReadState';

@Injectable({ providedIn: 'root' })
export class NotificationRepository {
  private readonly notificationService = inject(NotificationService);
  private readonly store = createStore(
    { name: 'notificationRepository' },
    withEntities<CompanyOrder, 'targetAccountName'>({
      idKey: 'targetAccountName',
    }),
    withUIEntities<NotificationUi, 'targetAccountName'>({
      idKey: 'targetAccountName',
    })
  );

  notifications$ = this.store.pipe(
    selectAllEntities(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  unread$ = this.store.pipe(
    selectEntitiesCountByPredicate(({ read }) => !read, { ref: UIEntitiesRef }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  count$ = this.store.pipe(
    selectEntitiesCount(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  get accountBasedLocalStorageKey() {
    return `${LOCAL_STORAGE_KEY}.${getCurrentAuthenticatedUser().accountName ?? ''}`;
  }

  constructor() {
    this.notificationService.receiveOrderNotificationEvent$.subscribe(() => {
      this.load();
    });
  }

  init() {
    this.load();
  }

  markAllRead() {
    this.store.update(
      updateAllEntities(
        { read: true },
        {
          ref: UIEntitiesRef,
        }
      )
    );
    this.writeLocalStorage();
  }

  private load() {
    subscribeOnce(
      AccountsApi.getCompanyOrders(PENDING_STATUS),
      (notifications) => {
        this.update(notifications);
      },
      (error) => {
        reportToTrackjs(
          'notification repository',
          'Could not load notifications.',
          error as Record<string, unknown>
        );
      }
    );
  }

  private update(notifications: CompanyOrder[]) {
    const alreadyRead = new Set(this.readLocalStorage());

    this.store.update(
      setEntities(notifications),
      setEntities(
        notifications.map(({ targetAccountName }) => ({
          targetAccountName,
          read: alreadyRead.has(targetAccountName),
        })),
        { ref: UIEntitiesRef }
      )
    );

    if (notifications.length === 0) {
      this.writeLocalStorage();
    }
  }

  private readLocalStorage() {
    const ids = localStorage.getItem(this.accountBasedLocalStorageKey);
    if (ids === null) {
      return [] as string[];
    }

    return JSON.parse(ids) as string[];
  }

  private writeLocalStorage() {
    const ids = this.store.query(
      getAllEntitiesApply({
        mapEntity: ({ targetAccountName }) => targetAccountName,
      })
    );

    if (ids.length === 0) {
      localStorage.removeItem(this.accountBasedLocalStorageKey);
      return;
    }

    localStorage.setItem(this.accountBasedLocalStorageKey, JSON.stringify(ids));
  }
}
